// @ts-ignore - no types for ar-feature-gating
import { splitClient } from '@nextroll/ar-feature-gating';

const DEFAULT_SPLIT_EID = 'ALL_USERS';
export const SPLIT_IO_READY_FALLBACK_TIMEOUT_MS = 3000;

const getSplitIoLoadOptions = (userId: string) => ({
    environment: process.env.ENVIRONMENT,
    advertisable: {
        eid: userId,
        organization: 'ORG_EID',
        business_unit: 'BUSINESS_UNIT',
    },
    user: {
        eid: userId,
    },
    actualUser: {
        eid: userId,
    },
});

const isSplitIoReadyForUser = (userId: string) => {
    const loadOptions = getSplitIoLoadOptions(userId);

    return (
        splitClient._ready &&
        splitClient._opts?.environment === loadOptions.environment &&
        splitClient._opts?.advertisable?.eid === loadOptions.advertisable.eid &&
        splitClient._opts?.advertisable?.organization ===
            loadOptions.advertisable.organization &&
        splitClient._opts?.advertisable?.business_unit ===
            loadOptions.advertisable.business_unit &&
        splitClient._opts?.user?.eid === loadOptions.user.eid &&
        splitClient._opts?.actualUser?.eid === loadOptions.actualUser.eid
    );
};

export const loadSplitIo = (
    userIdOrOnSplitClientReady: string | (() => void) = DEFAULT_SPLIT_EID,
    maybeOnSplitClientReady: () => void = () => {}
) => {
    const userId =
        typeof userIdOrOnSplitClientReady === 'string'
            ? userIdOrOnSplitClientReady
            : DEFAULT_SPLIT_EID;
    const onSplitClientReady =
        typeof userIdOrOnSplitClientReady === 'function'
            ? userIdOrOnSplitClientReady
            : maybeOnSplitClientReady;

    if (isSplitIoReadyForUser(userId)) {
        onSplitClientReady();
        return () => {};
    }

    let isSettled = false;
    let fallbackTimeoutId = 0;

    function cleanupSplitIoListeners() {
        window.clearTimeout(fallbackTimeoutId);
        splitClient.removeListener(
            splitClient.READY_EVENT,
            handleSplitClientReady
        );
        splitClient.removeListener(
            splitClient.LOAD_TIMEOUT_EVENT,
            handleSplitClientFailure
        );
        splitClient.removeListener(
            splitClient.LOAD_ERROR_EVENT,
            handleSplitClientFailure
        );
    }

    function settleSplitIoLoad() {
        if (isSettled) {
            return;
        }

        isSettled = true;
        cleanupSplitIoListeners();
        onSplitClientReady();
    }

    function handleSplitClientReady(event: { ready?: boolean } = {}) {
        if (event.ready === false) {
            return;
        }

        settleSplitIoLoad();
    }

    function handleSplitClientFailure() {
        settleSplitIoLoad();
    }

    splitClient.on(splitClient.READY_EVENT, handleSplitClientReady);
    splitClient.on(splitClient.LOAD_TIMEOUT_EVENT, handleSplitClientFailure);
    splitClient.on(splitClient.LOAD_ERROR_EVENT, handleSplitClientFailure);

    // Do not let Split block signup indefinitely when the SDK is unavailable.
    fallbackTimeoutId = window.setTimeout(
        handleSplitClientFailure,
        SPLIT_IO_READY_FALLBACK_TIMEOUT_MS
    );

    try {
        void splitClient
            .load(getSplitIoLoadOptions(userId))
            .catch(handleSplitClientFailure);
    } catch {
        handleSplitClientFailure();
    }

    return () => {
        if (isSettled) {
            return;
        }

        isSettled = true;
        cleanupSplitIoListeners();
    };
};

export const getTreatment = (feature: string) =>
    splitClient.getTreatment(feature);
